import { TColor } from "@dw/models/shared";
import { getColorCss } from "./stylingUtils";

export const getColor = (val?: TColor | string): string | undefined => {
    if (!val) {
        return undefined;
    }

    return val.startsWith('#') || val.startsWith('rgb') || val.startsWith('var(') ? val : `var(--color-${val})`;
}

/**
 * Parses a hex colour (#rgb or #rrggbb) into an [r, g, b] tuple. Returns null for anything that
 * isn't a recognisable hex string, so callers can fall back to a theme colour.
 */
const parseHexColor = (hex: string): [number, number, number] | null => {
    const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
    if (!match) {
        return null;
    }

    let value = match[1];
    if (value.length === 3) {
        value = value.split('').map(c => c + c).join('');
    }

    return [
        parseInt(value.slice(0, 2), 16),
        parseInt(value.slice(2, 4), 16),
        parseInt(value.slice(4, 6), 16)
    ];
};

/**
 * Picks black or white text for legibility on the given background colour, using relative luminance.
 * Falls back to white when the colour can't be parsed.
 */
export const getReadableTextColor = (backgroundHex: string): '#000000' | '#FFFFFF' => {
    const rgb = parseHexColor(backgroundHex);
    if (!rgb) {
        return '#FFFFFF';
    }

    const [r, g, b] = rgb;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? '#000000' : '#FFFFFF';
};

/**
 * WCAG relative luminance — the specified channel curve, not an average.
 *
 * Deliberately NOT the formula `getReadableTextColor` uses above. That one answers "black or white
 * on this?" with a cheap approximation and has callers depending on its exact behaviour; this one
 * has to produce a contrast RATIO a studio is shown as a number, so it follows the spec.
 */
const relativeLuminance = ([r, g, b]: [number, number, number]): number => {
    const channel = (value: number) => {
        const c = value / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };

    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/**
 * The WCAG contrast ratio between two colours, from 1 (identical) to 21 (black on white).
 *
 * Returns null when either colour can't be parsed, so a caller shows nothing rather than a
 * confidently wrong number while somebody is midway through typing a hex.
 */
export const getContrastRatio = (foregroundHex: string, backgroundHex: string): number | null => {
    const foreground = parseHexColor(foregroundHex);
    const background = parseHexColor(backgroundHex);

    if (!foreground || !background) {
        return null;
    }

    const foregroundLuminance = relativeLuminance(foreground);
    const backgroundLuminance = relativeLuminance(background);

    const lighter = Math.max(foregroundLuminance, backgroundLuminance);
    const darker = Math.min(foregroundLuminance, backgroundLuminance);

    return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Whether a background needs light chrome on it rather than dark.
 *
 * The threshold is the point where contrast against white and against black are equal, and it
 * mirrors `NoticeThemeHelpers.IsDarkBackground` on the API — the two must agree, because the API
 * uses it to decide which built-in tint an old app build is told a custom theme is, and the portal
 * uses it to decide whether a preview's tape flips.
 */
export const isDarkBackgroundColor = (backgroundHex: string): boolean => {
    const rgb = parseHexColor(backgroundHex);

    return !!rgb && relativeLuminance(rgb) < 0.1791;
};

/**
 * Appends an alpha channel to a hex colour, returning an 8-digit hex (e.g. "#008080B8").
 * Returns the input unchanged when it isn't a plain hex colour.
 */
export const withAlpha = (hex: string, alpha: number): string => {
    const rgb = parseHexColor(hex);
    if (!rgb) {
        return hex;
    }

    const clamped = Math.max(0, Math.min(1, alpha));
    const alphaHex = Math.round(clamped * 255).toString(16).padStart(2, '0');
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${rgb.map(toHex).join('')}${alphaHex}`;
};

/**
 * Builds the diagonal brand banner gradient from a single hex colour — shared by the public invoice
 * letterhead and the settings colour preview so both render an identical band.
 */
export const getBrandBannerBackground = (hex: string): string =>
    `linear-gradient(-35deg, ${hex}, ${withAlpha(hex, 0.72)})`;

/**
 * Returns a brand hex colour when it's a valid #rgb / #rrggbb string (whitespace trimmed), otherwise
 * `undefined` so callers can fall back to a theme colour or omit an override entirely.
 */
export const resolveBrandColor = (color?: string | null): string | undefined => {
    const trimmed = color?.trim();
    return trimmed && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmed) ? trimmed : undefined;
};

/**
 * Resolves a school's brand colour for the public invoice, falling back to the theme teal (the app
 * primary) when the school hasn't chosen one. Pass `opacity` for tinted fills/borders — the brand
 * hex is returned with alpha applied, or the theme primary as an rgba().
 */
export const getBrandColorCss = (color: string | null | undefined, opacity?: number): string => {
    const hex = resolveBrandColor(color);
    if (hex) {
        return opacity === undefined ? hex : withAlpha(hex, opacity);
    }
    return getColorCss('primary', opacity);
};
